import { Server as ServerType, serversApi, ServerStats } from '../../lib/api/servers';
import { Server, MoreVertical, Wifi, WifiOff, Cpu, HardDrive, MemoryStick, Clock, Box, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useTestConnection, useDeleteServer } from '../../hooks/useServers';
import { Button } from '../ui/Button';
import { ConfirmModal } from '../ui/ConfirmModal';

interface ServerCardProps {
  server: ServerType;
}

export function ServerCard({ server }: ServerCardProps) {
  const [showMenu, setShowMenu] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'testing' | 'success' | 'failed'>('idle');
  const [stats, setStats] = useState<ServerStats | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [containerCount, setContainerCount] = useState<number>(0);
  const testConnection = useTestConnection();
  const deleteServer = useDeleteServer();

  // Fetch server stats on mount and every 30 seconds
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [statsData, containersData] = await Promise.all([
          serversApi.getStats(server.id),
          serversApi.getContainers(server.id),
        ]);
        setStats(statsData);
        setContainerCount(containersData.count || 0);
      } catch (error) {
        console.error('Failed to fetch server stats:', error);
      }
    };

    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, [server.id]);

  const handleTest = async () => {
    setConnectionStatus('testing');
    try {
      const result = await testConnection.mutateAsync(server.id);
      setConnectionStatus(result.success ? 'success' : 'failed');
    } catch {
      setConnectionStatus('failed');
    }
  };

  const handleDeleteClick = () => {
    setShowMenu(false);
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    await deleteServer.mutateAsync(server.id);
    setShowDeleteConfirm(false);
  };

  // Parse resource usage percentages
  const parsePercentage = (value: string | null | undefined): number => {
    if (!value) return 0;
    const match = value.match(/(\d+(?:\.\d+)?)/);
    return match ? parseFloat(match[1]) : 0;
  };

  const cpuPercent = stats?.load_average ? parsePercentage(stats.load_average) : 0;
  const memoryPercent = stats?.memory_usage ? parsePercentage(stats.memory_usage) : 0;
  const diskPercent = stats?.disk_usage ? parsePercentage(stats.disk_usage) : 0;

  const getColorClass = (percent: number) => {
    if (percent >= 85) return 'bg-red-500';
    if (percent >= 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const hasStats = stats && (stats.load_average || stats.memory_usage || stats.disk_usage);
  const isOnline = hasStats;

  return (
    <Link to={`/servers/${server.id}`}>
      <div
        className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-lg hover:border-primary-300 transition-all cursor-pointer group"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3 flex-1">
            <div className={`p-2 rounded-lg ${isOnline ? 'bg-primary-50' : 'bg-gray-50'} group-hover:scale-110 transition-transform`}>
              <Server className={`h-5 w-5 ${isOnline ? 'text-primary-600' : 'text-gray-400'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className={`h-2.5 w-2.5 rounded-full ${isOnline ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} />
                <h3 className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors truncate">
                  {server.name}
                </h3>
                {server.is_local && (
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                    Local
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                {server.username}@{server.host}:{server.port}
              </p>
            </div>
          </div>

          <div className="relative" onClick={(e) => e.preventDefault()}>
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1 hover:bg-gray-100 rounded opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <MoreVertical className="h-4 w-4 text-gray-400" />
            </button>

            {showMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowMenu(false)}
                />
                <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg border z-20">
                  <Link
                    to={`/servers/${server.id}`}
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    📊 View Details
                  </Link>
                  <Link
                    to={`/servers/${server.id}/edit`}
                    className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    ✏️ Edit
                  </Link>
                  <button
                    onClick={handleTest}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    🔄 Test Connection
                  </button>
                  <button
                    onClick={handleDeleteClick}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    🗑️ Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Status Box */}
        <div className="bg-gray-50 rounded-lg p-3 space-y-2.5">
          {isOnline ? (
            <>
              {/* Resource Metrics */}
              <div className="space-y-2">
                {/* CPU */}
                {stats.load_average && (
                  <div className="flex items-center gap-2">
                    <Cpu className="h-3.5 w-3.5 text-gray-500" />
                    <span className="text-xs text-gray-600 w-12">CPU:</span>
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${getColorClass(cpuPercent)}`}
                        style={{ width: `${Math.min(cpuPercent, 100)}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-700 font-medium w-12 text-right">
                      {stats.load_average}
                    </span>
                  </div>
                )}

                {/* Memory */}
                {stats.memory_usage && (
                  <div className="flex items-center gap-2">
                    <MemoryStick className="h-3.5 w-3.5 text-gray-500" />
                    <span className="text-xs text-gray-600 w-12">Memory:</span>
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-500 h-2 rounded-full transition-all"
                        style={{ width: `${Math.min(memoryPercent, 100)}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-700 font-medium w-12 text-right">
                      {stats.memory_usage}
                    </span>
                  </div>
                )}

                {/* Disk */}
                {stats.disk_usage && (
                  <div className="flex items-center gap-2">
                    <HardDrive className="h-3.5 w-3.5 text-gray-500" />
                    <span className="text-xs text-gray-600 w-12">Disk:</span>
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${getColorClass(diskPercent)}`}
                        style={{ width: `${Math.min(diskPercent, 100)}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-700 font-medium w-12 text-right">
                      {stats.disk_usage}
                    </span>
                  </div>
                )}
              </div>

              {/* Additional Info */}
              <div className="pt-2 border-t border-gray-200 flex items-center justify-between text-xs">
                {stats.uptime && (
                  <div className="flex items-center gap-1.5 text-gray-600">
                    <Clock className="h-3 w-3" />
                    <span>Uptime: {stats.uptime}</span>
                  </div>
                )}
                {containerCount > 0 && (
                  <div className="flex items-center gap-1.5 text-primary-600">
                    <Box className="h-3 w-3" />
                    <span>{containerCount} container{containerCount !== 1 ? 's' : ''}</span>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="text-center py-2">
              <WifiOff className="h-8 w-8 text-gray-300 mx-auto mb-2" />
              <p className="text-xs text-gray-500 font-medium">Offline or stats unavailable</p>
              <p className="text-xs text-gray-400 mt-1">Unable to fetch server metrics</p>
            </div>
          )}
        </div>

        {/* Description (if exists) */}
        {server.description && (
          <p className="mt-3 text-xs text-gray-500 line-clamp-2">
            {server.description}
          </p>
        )}

        {/* View Details Arrow (appears on hover) */}
        <div className="mt-3 flex items-center justify-end opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="text-xs text-primary-600 font-medium flex items-center gap-1">
            View Details
            <ChevronRight className="h-3 w-3" />
          </span>
        </div>

        {/* Delete Confirmation Modal */}
        <ConfirmModal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Server"
          message={
            <>
              Are you sure you want to delete <strong>{server.name}</strong>?
              This action cannot be undone.
            </>
          }
          confirmText="Delete"
          variant="danger"
          isLoading={deleteServer.isPending}
        />
      </div>
    </Link>
  );
}
